import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Swal from "sweetalert2";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../../../../../firebaseConfig";
import { useBrandsStore, type Command, type CommandFormData } from "../../../../../../store/reseller-admin/brandsStore";
import { useUserStore } from "../../../../../../store/userStore";
import { useRolesStore } from "@/store/admin/rolesStore";
import type { ThemeClasses, Brand, AlertState } from "../types";
import Modal from "../../../../../../components/base/Modal";
import CustomAlert from "../../../../../../components/base/CustomAlert";
import { getCustomSelectStyles } from "../../../../shared/index";

interface CommandsModalProps {
  brand: Brand;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
}

type ViewMode = "list" | "add" | "edit";

const initialCommandForm: CommandFormData = {
  code: "",
  name: "",
  data: false,
  password_required: false,
  roles: [],
  transport: {
    value: "",
  },
  sms_enabled: false,
  sms_body: null,
};

export function CommandsModal({
  brand,
  themeClasses,
  isDarkMode,
  onClose,
}: CommandsModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const user = useUserStore((state) => state.user);

  const {
    commands,
    commandsLoading,
    fetchCommands,
    createCommand,
    updateCommand,
    deleteCommand,
    clearCommands,
  } = useBrandsStore();

  // View states
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [smsInherit, setSmsInherit] = useState(true);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  // Auth validation states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUsername, setAuthUsername] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showAuthPassword, setShowAuthPassword] = useState(false);

  // Roles from store (cached)
  const {
    rolesBrands: rolesOptions,
    rolesBrandsLoading: rolesLoading,
    fetchRolesBrands,
  } = useRolesStore();

  const codeOptions = [
    "reboot_device",
    "engine_stop",
    "engine_resume",
    "request_status",
    "request_version",
    "output_1_on",
    "output_1_off",
    "output_2_on",
    "output_2_off",
    "set_primary_server",
    "set_secondary_server",
    "set_apn",
    "set_time_zone",
    "set_reporting_interval",
    "set_overspeed_limit",
    "clear_overspeed_limit",
    "set_sleep_mode",
    "request_imei",
    "request_iccid",
    "request_signal",
    "request_gps_status",
    "request_inputs_outputs",
    "custom_raw",
    "request_position"
  ];

  const codeOptionsWithLabels = useMemo(() => {
    return codeOptions.map(cmd => ({
      value: cmd,
      label: t(`commands.codes.${cmd}`)
    }));
  }, [t]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CommandFormData>({
    defaultValues: initialCommandForm,
  });

  const smsEnabled = watch("sms_enabled");
  const transportValue = watch("transport.value");

  // Load commands when modal opens
  useEffect(() => {
    if (brand.id) {
      fetchCommands(brand.id);
    }
    return () => {
      clearCommands();
    };
  }, [brand.id, fetchCommands, clearCommands]);

  // Load roles from store (only fetches once, then cached)
  useEffect(() => {
    fetchRolesBrands();
  }, [fetchRolesBrands]);

  // Handle authentication
  const handleAuthenticate = async () => {
    setAuthError(null);
    setIsAuthenticating(true);

    try {
      // Verify username or email matches current user
      const currentUsername = user?.username?.toLowerCase();
      const currentEmail = user?.email?.toLowerCase();
      const inputValue = authUsername.toLowerCase().trim();

      // Check if input matches either username or email
      const isUsernameMatch = currentUsername && inputValue === currentUsername;
      const isEmailMatch = currentEmail && inputValue === currentEmail;

      if (!isUsernameMatch && !isEmailMatch) {
        setAuthError(t("commands.auth.userNotMatch"));
        setIsAuthenticating(false);
        return;
      }

      // Verify password using Firebase
      const email = user?.email;
      if (!email) {
        setAuthError(t("commands.auth.noEmail"));
        setIsAuthenticating(false);
        return;
      }

      await signInWithEmailAndPassword(auth, email, authPassword);
      setIsAuthenticated(true);
      setAuthError(null);
    } catch (err: any) {
      console.error("Auth error:", err);
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setAuthError(t("commands.auth.wrongPassword"));
      } else {
        setAuthError(t("commands.auth.authError"));
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleAddClick = () => {
    reset(initialCommandForm);
    setSmsInherit(true);
    setSelectedCommand(null);
    setViewMode("add");
  };

  const handleEditClick = (command: Command) => {
    setSelectedCommand(command);
    const inherit = command.sms_body === null || command.sms_body === undefined || command.sms_body === "";
    setSmsInherit(inherit);
    reset({
      code: command.code || "",
      name: command.name,
      data: command.data,
      password_required: command.password_required,
      roles: command.roles || [],
      transport: {
        value: command.transport?.value || "",
      },
      sms_enabled: command.sms_enabled ?? false,
      sms_body: inherit ? null : command.sms_body,
    });
    setViewMode("edit");
  };

  const handleDeleteClick = async (command: Command) => {
    const confirmResult = await Swal.fire({
      title: t("commands.alerts.confirmDeleteTitle"),
      text: t("commands.confirmDelete", { name: command.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("commands.alerts.confirmDeleteBtn"),
      cancelButtonText: t("commands.alerts.cancelBtn"),
    });

    if (confirmResult.isConfirmed) {
      const result = await deleteCommand(brand.id, command.id);
      if (result.success) {
        setAlert({
          show: true,
          title: t("commands.alerts.success"),
          message: t("commands.alerts.deleteSuccess"),
          type: "success",
        });
      } else {
        setAlert({
          show: true,
          title: t("commands.alerts.error"),
          message: result.error || t("commands.deleteError"),
          type: "error",
        });
      }
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedCommand(null);
    reset(initialCommandForm);
    setSmsInherit(true);
    setError(null);
  };

  const onSubmit = async (formData: CommandFormData) => {
    setIsSubmitting(true);
    setError(null);

    const data: CommandFormData = {
      ...formData,
      sms_body: !formData.sms_enabled || smsInherit ? null : (formData.sms_body || null),
    };

    try {
      if (viewMode === "add") {
        const result = await createCommand(brand.id, data);
        if (result.success) {
          setAlert({
            show: true,
            title: t("commands.alerts.success"),
            message: t("commands.alerts.createSuccess"),
            type: "success",
          });
          handleBackToList();
        } else {
          setAlert({
            show: true,
            title: t("commands.alerts.error"),
            message: result.error || t("commands.createError"),
            type: "error",
          });
        }
      } else if (viewMode === "edit" && selectedCommand) {
        const result = await updateCommand(brand.id, selectedCommand.id, data);
        if (result.success) {
          setAlert({
            show: true,
            title: t("commands.alerts.success"),
            message: t("commands.alerts.updateSuccess"),
            type: "success",
          });
          handleBackToList();
        } else {
          setAlert({
            show: true,
            title: t("commands.alerts.error"),
            message: result.error || t("commands.updateError"),
            type: "error",
          });
        }
      }
    } catch (err) {
      setAlert({
        show: true,
        title: t("commands.alerts.error"),
        message: t("commands.generalError"),
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const getInputClassName = (hasError: boolean) =>
    `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${hasError ? "border-red-500" : themeClasses.cardBorder
    } rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 ${hasError ? "focus:ring-red-500" : "focus:ring-primary-500"
    }`;

  const errorClassName = "text-red-500 text-xs mt-1";

  // Alert Component
  const AlertComponent = alert.show ? (
    <CustomAlert
      title={alert.title}
      text={alert.message}
      icon={alert.type}
      onClose={closeAlert}
    />
  ) : null;

  // Auth View
  if (!isAuthenticated) {
    return (
      <>
        {AlertComponent}
        <Modal
        width="w-full max-w-md"
        content={
          <div className="space-y-6">
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? "bg-yellow-900/30" : "bg-yellow-100"} text-yellow-500 rounded-full flex items-center justify-center`}>
                <i className="ri-shield-keyhole-line text-3xl"></i>
              </div>
              <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>
                {t("commands.auth.title")}
              </h3>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("commands.auth.description")}
              </p>
            </div>

            {authError && (
              <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                {authError}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex flex-col">
                <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("commands.auth.username")}
                </label>
                <input
                  type="text"
                  value={authUsername}
                  onChange={(e) => setAuthUsername(e.target.value)}
                  className={getInputClassName(false)}
                  placeholder={t("commands.auth.usernamePlaceholder")}
                  onKeyDown={(e) => e.key === "Enter" && authUsername && authPassword && handleAuthenticate()}
                  autoFocus
                />
              </div>

              <div className="flex flex-col">
                <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("commands.auth.configPassword")}
                </label>
                <div className="relative">
                  <input
                    type={showAuthPassword ? "text" : "password"}
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    className={`${getInputClassName(false)} pr-10`}
                    placeholder={t("commands.auth.passwordPlaceholder")}
                    onKeyDown={(e) => e.key === "Enter" && authUsername && authPassword && handleAuthenticate()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAuthPassword(!showAuthPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} hover:${themeClasses.text}`}
                  >
                    <i className={showAuthPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAuthenticate}
                disabled={isAuthenticating || !authUsername || !authPassword}
                className="w-full px-4 py-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
              {isAuthenticating ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  {t("commands.auth.authenticating")}
                </span>
              ) : (
                <>
                  <i className="ri-login-box-line mr-2"></i>
                  {t("commands.auth.authenticate")}
                </>
              )}
              </button>
            </div>
          </div>
        }
        themeClasses={themeClasses}
        onClose={onClose}
        title={t("commands.title")}
      />
      </>
    );
  }

  // List View
  if (viewMode === "list") {
    return (
      <>
        {AlertComponent}
        <Modal
        width="w-full max-w-3xl"
        content={
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Header with add button */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className={`font-semibold ${themeClasses.text}`}>
                  {brand.brand} - {brand.model}
                </h3>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {commands.length} {t("commands.commandsCount")}
                </p>
              </div>
              <button
                onClick={handleAddClick}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors"
              >
                <i className="ri-add-line mr-1"></i>
                {t("commands.addCommand")}
              </button>
            </div>

            {/* Commands list */}
            {commandsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : commands.length === 0 ? (
              <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
                <i className="ri-terminal-box-line text-4xl mb-3 block opacity-50"></i>
                <p>{t("commands.noCommands")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {commands.map((command) => (
                  <div
                    key={command.id}
                    className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-4`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium ${themeClasses.text}`}>
                            {command.name}
                          </h4>
                          <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            {t("commands.fields.code")}: {command.code}
                          </span>
                        </div>
                        <p className={`text-sm ${themeClasses.textSecondary} mt-1 font-mono`}>
                          {t("commands.fields.transportValue")}: {command.transport?.value}
                        </p>
                        <div className="flex items-center flex-wrap gap-2 mt-2">
                          <span className={`text-xs px-2 py-1 rounded ${command.enabled ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                            {command.enabled ? t("commands.enabled") : t("commands.disabled")}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${command.data ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                            {t("commands.fields.data")}: {command.data ? "ON" : "OFF"}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${command.password_required ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                            {command.password_required ? t("commands.passwordRequired") : t("commands.noPassword")}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${command.sms_enabled ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                            <i className="ri-message-2-line mr-1"></i>
                            {command.sms_enabled ? t("commands.enabled") : t("commands.disabled")}
                          </span>
                          {command.roles?.length > 0 && (
                            <span className={`text-xs ${themeClasses.textSecondary}`}>
                              {command.roles.length} {t("commands.roles")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditClick(command)}
                          className={`p-2 ${themeClasses.buttonHover} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(command)}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-500"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        }
        themeClasses={themeClasses}
        onClose={onClose}
        title={t("commands.title")}
      />
      </>
    );
  }

  // Add/Edit Form View
  return (
    <>
      {AlertComponent}
      <Modal
      width="w-full max-w-2xl"
      content={
        <form
          id="command-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
        >
          <button
            type="button"
            onClick={handleBackToList}
            className={`flex items-center ${themeClasses.textSecondary} hover:${themeClasses.text} mb-4`}
          >
            <i className="ri-arrow-left-line mr-2"></i>
            {t("commands.backToList")}
          </button>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Code */}
          <div className="flex flex-col">
            <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
              {t("commands.fields.code")} <span className="text-red-500">*</span>
            </label>
            <Controller
              name="code"
              control={control}
              rules={{ required: t("commands.validation.codeRequired") }}
              render={({ field }) => (
                <Select
                  options={codeOptionsWithLabels}
                  styles={getCustomSelectStyles(isDarkMode, !!errors.code)}
                  placeholder={t("commands.placeholders.selectCode")}
                  value={codeOptionsWithLabels.find((opt: any) => opt.value === field.value) || null}
                  onChange={(option: any) => field.onChange(option?.value || "")}
                  isDisabled={viewMode === "edit"}
                  noOptionsMessage={() => t("commands.noCodeAvailable")}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />
            {errors.code && (
              <span className={errorClassName}>{errors.code.message}</span>
            )}
            {viewMode === "edit" && (
              <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                {t("commands.fields.codeNotEditable")}
              </p>
            )}
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              {t("commands.fields.codeDesc")}
            </p>
          </div>

          {/* Name */}
          <div className="flex flex-col">
            <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
              {t("commands.fields.name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={getInputClassName(!!errors.name)}
              placeholder={t("commands.placeholders.name")}
              {...register("name", {
                required: t("commands.validation.nameRequired"),
              })}
            />
            {errors.name && (
              <span className={errorClassName}>{errors.name.message}</span>
            )}
          </div>

          {/* Transport Value (Command/Message) - Console style */}
          <div className="flex flex-col">
            <label className={`text-sm font-medium ${themeClasses.text} mb-1 flex items-center gap-2`}>
              <i className={`ri-terminal-line ${isDarkMode ? "text-green-400" : "text-green-600"}`}></i>
              {t("commands.fields.transportValue")} <span className="text-red-500">*</span>
            </label>
            <div className="px-1 mt-1">
              <textarea
                className={`w-full px-5 py-4 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none bg-[#1e1e1e] text-green-400 placeholder-gray-500 border ${errors.transport?.value ? "border-red-500" : isDarkMode ? "border-gray-600" : "border-gray-300"
                  }`}
                placeholder={t("commands.placeholders.transportValue")}
                rows={3}
                {...register("transport.value", {
                  required: t("commands.validation.transportValueRequired"),
                })}
              />
            </div>
            {errors.transport?.value && (
              <span className={errorClassName}>{errors.transport.value.message}</span>
            )}
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              {t("commands.fields.transportValueHelp")}
            </p>
          </div>

          {/* SMS Configuration */}
          <div className={`flex flex-col gap-4 p-4 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} rounded-lg`}>
            {/* SMS Enabled toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
                  <i className="ri-message-2-line"></i>
                  {t("commands.fields.smsEnabled")}
                </label>
                <p className={`text-xs ${themeClasses.textSecondary}`}>
                  {t("commands.fields.smsEnabledDesc")}
                </p>
              </div>
              <Controller
                name="sms_enabled"
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    onClick={() => field.onChange(!field.value)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${field.value ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"}`}
                  >
                    <span className={`absolute top-1 ${field.value ? "right-1" : "left-1"} w-4 h-4 bg-white rounded-full transition-all`}></span>
                  </button>
                )}
              />
            </div>

            {/* SMS Body (only when SMS enabled) */}
            {smsEnabled && (
              <div className="flex flex-col gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                {/* Inherit toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.text}`}>
                      {t("commands.fields.smsInherit")}
                    </label>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("commands.fields.smsInheritDesc")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !smsInherit;
                      setSmsInherit(next);
                      if (next) {
                        setValue("sms_body", null);
                      } else {
                        setValue("sms_body", transportValue || "");
                      }
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${smsInherit ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"}`}
                  >
                    <span className={`absolute top-1 ${smsInherit ? "right-1" : "left-1"} w-4 h-4 bg-white rounded-full transition-all`}></span>
                  </button>
                </div>

                {smsInherit ? (
                  <div className="px-1">
                    <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("commands.fields.smsBodyInherited")}
                    </p>
                    <div className="w-full px-5 py-4 rounded-lg font-mono text-sm resize-none bg-[#1e1e1e] text-green-400 border border-gray-600 opacity-70 whitespace-pre-wrap break-words">
                      {transportValue || t("commands.placeholders.transportValue")}
                    </div>
                  </div>
                ) : (
                  <div className="px-1">
                    <label className={`text-sm font-medium ${themeClasses.text} mb-1 flex items-center gap-2`}>
                      <i className={`ri-terminal-line ${isDarkMode ? "text-green-400" : "text-green-600"}`}></i>
                      {t("commands.fields.smsBody")}
                    </label>
                    <textarea
                      className="w-full px-5 py-4 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none bg-[#1e1e1e] text-green-400 placeholder-gray-500 border border-gray-600"
                      placeholder={t("commands.placeholders.smsBody")}
                      rows={3}
                      {...register("sms_body")}
                    />
                    <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                      {t("commands.fields.smsBodyHelp")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Toggles - Grid 2x2 */}
          <div className="grid grid-cols-2 gap-4">
            {/* Password Required */}
            <div className={`flex items-center justify-between p-4 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} rounded-lg`}>
              <div>
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("commands.fields.passwordRequired")}
                </label>
                <p className={`text-xs ${themeClasses.textSecondary}`}>
                  {t("commands.fields.passwordRequiredDesc")}
                </p>
              </div>
              <Controller
                name="password_required"
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    onClick={() => field.onChange(!field.value)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${field.value ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"}`}
                  >
                    <span className={`absolute top-1 ${field.value ? "right-1" : "left-1"} w-4 h-4 bg-white rounded-full transition-all`}></span>
                  </button>
                )}
              />
            </div>

            {/* Data */}
            <div className={`flex items-center justify-between p-4 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} rounded-lg`}>
              <div>
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("commands.fields.data")}
                </label>
                <p className={`text-xs ${themeClasses.textSecondary}`}>
                  {t("commands.fields.dataDesc")}
                </p>
              </div>
              <Controller
                name="data"
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    onClick={() => field.onChange(!field.value)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${field.value ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"}`}
                  >
                    <span className={`absolute top-1 ${field.value ? "right-1" : "left-1"} w-4 h-4 bg-white rounded-full transition-all`}></span>
                  </button>
                )}
              />
            </div>
          </div>

          {/* Roles */}
          <div className="flex flex-col">
            <label className={`text-sm font-medium ${themeClasses.text} mb-1`}>
              {t("commands.fields.roles")}
            </label>
            <Controller
              name="roles"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={rolesOptions}
                  styles={getCustomSelectStyles(isDarkMode, false)}
                  placeholder={t("commands.placeholders.selectRoles")}
                  value={rolesOptions.filter((opt) => field.value?.includes(opt.value))}
                  onChange={(options: any) => field.onChange(options?.map((o: any) => o.value) || [])}
                  isLoading={rolesLoading}
                  noOptionsMessage={() => t("commands.noRolesAvailable")}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              {t("commands.fields.rolesDesc")}
            </p>
          </div>
        </form>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={viewMode === "add" ? t("commands.addTitle") : t("commands.editTitle")}
      buttonAction={
        <button
          type="submit"
          form="command-form"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {isSubmitting ? t("commands.saving") : viewMode === "add" ? t("commands.create") : t("commands.saveChanges")}
        </button>
      }
    />
    </>
  );
}
